import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useSound } from "../../../../../../context/juno/SoundContext";
import { Clear } from "@mui/icons-material";

// プリセットタグの定義
const presetTags = {
  genres: [
    "lofi",
    "funk",
    "pop",
    "j-pop",
    "k-pop",
    "soul",
    "rock",
    "jazz",
    "electronic",
    "hip-hop",
    "r&b",
    "classical",
    "ambient",
    "blues",
    "country",
    "folk",
    "reggae",
    "metal",
    "punk",
    "indie",
    "latin",
    "synthwave",
    "chillwave",
    "house",
    "trance",
    "dubstep",
    "drum and bass",
  ],
  instruments: [
    "guitar",
    "drums",
    "bass",
    "keyboard",
    "percussion",
    "piano",
    "strings",
    "synth",
    "vocal",
    "brass",
    "woodwind",
    "flute",
    "saxophone",
    "trumpet",
    "violin",
    "cello",
    "ukulele",
    "banjo",
    "accordion",
    "harp",
    "mandolin",
    "sitar",
    "theremin",
    "sax",
    "synthesizer",
    "drum machine",
  ],
  tempo: ["90 BPM", "100 BPM", "105 BPM", "120 BPM", "140 BPM", "160 BPM"],
  mood: [
    "energetic",
    "upbeat",
    "groovy",
    "vibrant",
    "dynamic",
    "mellow",
    "chill",
    "atmospheric",
    "intense",
    "cinema",
    "nostalgic",
    "romantic",
    "ballad",
    "dark",
    "epic",
    "dramatic",
    "happy",
    "sad",
  ],
};

// タグ入力コントロール
const TagsControl: React.FC = () => {
  const { t } = useTranslation();
  const { tags, setTags } = useSound();
  const [expanded, setExpanded] = useState<string | false>(false);

  // タグを更新
  const handleTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTags(event.target.value);
  };

  // タグをすべて削除
  const clearAllTags = () => {
    setTags("");
  };

  // タグがすでに含まれているか確認
  const isTagIncluded = (tag: string): boolean => {
    return tags
      .split(",")
      .map((t) => t.trim())
      .includes(tag);
  };

  // タグをトグル（追加/削除）
  const toggleTag = (tag: string) => {
    const currentTags = tags
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t !== "");

    if (isTagIncluded(tag)) {
      // タグを削除
      const newTags = currentTags.filter((t) => t !== tag);
      setTags(newTags.join(", "));
    } else {
      // タグを追加
      const newTags = [...currentTags, tag];
      setTags(newTags.join(", "));
    }
  };

  // アコーディオンの展開状態を変更
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      <FormControl fullWidth>
        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          {t("juno.sound.params.tags")}
        </Typography>

        {/* プリセットタグセクション */}
        <Box>
          {/* ジャンル */}
          <Accordion
            expanded={expanded === "genres"}
            onChange={handleAccordionChange("genres")}
            sx={{ mb: 1, background: "transparent", boxShadow: "none" }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="genres-content"
              id="genres-header"
              sx={{
                minHeight: 36,
                py: 0,
                "& .MuiAccordionSummary-content": {
                  margin: "6px 0",
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                {t("juno.sound.params.tagGenres")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {presetTags.genres.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => toggleTag(tag)}
                    color={isTagIncluded(tag) ? "primary" : "default"}
                    variant={isTagIncluded(tag) ? "filled" : "outlined"}
                    sx={{ m: 0.3 }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* 楽器 */}
          <Accordion
            expanded={expanded === "instruments"}
            onChange={handleAccordionChange("instruments")}
            sx={{ mb: 1, background: "transparent", boxShadow: "none" }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="instruments-content"
              id="instruments-header"
              sx={{
                minHeight: 36,
                py: 0,
                "& .MuiAccordionSummary-content": {
                  margin: "6px 0",
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                {t("juno.sound.params.tagInstruments")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {presetTags.instruments.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => toggleTag(tag)}
                    color={isTagIncluded(tag) ? "primary" : "default"}
                    variant={isTagIncluded(tag) ? "filled" : "outlined"}
                    sx={{ m: 0.3 }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* テンポ */}
          <Accordion
            expanded={expanded === "tempo"}
            onChange={handleAccordionChange("tempo")}
            sx={{ mb: 1, background: "transparent", boxShadow: "none" }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="tempo-content"
              id="tempo-header"
              sx={{
                minHeight: 36,
                py: 0,
                "& .MuiAccordionSummary-content": {
                  margin: "6px 0",
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                {t("juno.sound.params.tagTempo")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {presetTags.tempo.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => toggleTag(tag)}
                    color={isTagIncluded(tag) ? "primary" : "default"}
                    variant={isTagIncluded(tag) ? "filled" : "outlined"}
                    sx={{ m: 0.3 }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* 雰囲気 */}
          <Accordion
            expanded={expanded === "mood"}
            onChange={handleAccordionChange("mood")}
            sx={{ mb: 1, background: "transparent", boxShadow: "none" }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="mood-content"
              id="mood-header"
              sx={{
                minHeight: 36,
                py: 0,
                "& .MuiAccordionSummary-content": {
                  margin: "6px 0",
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                {t("juno.sound.params.tagMood")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {presetTags.mood.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => toggleTag(tag)}
                    color={isTagIncluded(tag) ? "primary" : "default"}
                    variant={isTagIncluded(tag) ? "filled" : "outlined"}
                    sx={{ m: 0.3 }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
        <TextField
          multiline
          maxRows={4}
          value={tags}
          onChange={handleTagsChange}
          placeholder={t("juno.sound.params.tagsPlaceholder")}
          variant="standard"
          size="small"
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: tags && (
              <InputAdornment position="end">
                <IconButton onClick={clearAllTags} size="small" edge="end" sx={{}}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormHelperText sx={{ mb: 2 }}>{t("juno.sound.params.tagsHelp")}</FormHelperText>
      </FormControl>
    </Box>
  );
};

export default TagsControl;
